/**
 * «Min konto» (owner, 2026-11-01): the staff member's own Vipps link for «Logg inn med Vipps».
 *  - Linked: «Koblet til Vipps dd.mm.yyyy» and «Fjern kobling» (two clicks; the password keeps working).
 *  - Not linked, Vipps Login set up for the mosque: «Koble til Vipps» goes to Vipps and comes back here (App spends the
 *    code with this session). Only what Vipps calls the person (`sub`) is kept, as a keyed code — never phone or name.
 *  - Not set up: one sentence why there is no button.
 * Results are toasts (2026-10-31). The server's routes: GET /admin/auth/vipps/link, POST /admin/auth/vipps/unlink.
 */
import React, { useEffect, useState } from 'react';
import { call, post } from '../lib/api';
import { osloDay } from '../lib/format';
import { toast } from '../lib/toast';
import { startVipps } from '../lib/vipps-login';
import { FormDialog } from './dialog';
import { Btn, TwoStep } from './ui';

interface Link { configured: boolean; linked: boolean; linkedAt: string | null }

export function MyAccountDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [link, setLink] = useState<Link | null>(null);
  const load = () => call<Link>('/auth/vipps/link').then(setLink).catch((e) => toast((e as Error).message || 'Kunne ikke hente kontoen.', 'err'));
  useEffect(() => { if (open) { setLink(null); void load(); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <FormDialog open={open} title="Min konto" onClose={onClose}
      hint="Logg inn med Vipps i stedet for passord. Koblingen gjelder bare din egen konto, og bare Vipps-innloggingen din — passordet virker som før.">
      {!link && <p className="note">Henter …</p>}
      {link && link.linked && <>
        <p><b>Koblet til Vipps {osloDay(link.linkedAt)}</b></p>
        <p className="note">Neste gang kan du velge «Logg inn med Vipps». Fjerner du koblingen, logger du inn med e-post og passord.</p>
        <div className="acts">
          <TwoStep label="Fjern kobling" ariaLabel="Fjern Vipps-koblingen" busyLabel="Fjerner …" confirmLabel="Klikk igjen: Vipps kobles fra kontoen din"
            onConfirm={async () => {
              const r = await post('/auth/vipps/unlink');
              toast(r.ok ? r.body.message_no : (r.body.message_no || r.body.message || 'Koblingen ble ikke fjernet.'), r.ok ? 'ok' : 'err');
              void load();
            }} />
        </div>
      </>}
      {link && !link.linked && link.configured && <>
        <p className="note">Ikke koblet til Vipps. Du logger inn i Vipps, og vi lagrer bare en kode som sier at det er deg — ikke telefonnummer eller navn.</p>
        <div className="acts">
          <Btn onClick={async () => { const e = await startVipps('link'); if (e) toast(e, 'err'); }}>Koble til Vipps</Btn>
        </div>
      </>}
      {link && !link.linked && !link.configured && <p className="note">Vipps-innlogging er ikke satt opp for denne moskeen.</p>}
    </FormDialog>
  );
}
